/**
 *
 * Example usage:
 * ```
 * <Button></Button>
 * ```
 *
 * @packageDocumentation
 * @module components/Button
 */
import { forwardRef } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { isNullish } from '@sas/nova-core/typeguards';
import { ButtonState, ButtonVariant } from '.';
import { renderDesignTokens } from '../../theme';
import { BaseButton } from './__internal__';
import { jsx as _jsx } from "react/jsx-runtime";
const logger = getLogger('Button');
export const Button = /*#__PURE__*/forwardRef(function Button(props, ref) {
  if (isNullish(props.icon) && (isNullish(props.text) || props.text.trim() === '')) {
    logger.error('An icon or text must be set, and text cannot be blank.');
  }
  return /*#__PURE__*/_jsx(BaseButton, {
    ...props,
    ref: ref
  });
});

/**
 *
 * @param dt - DesignToken object
 * @param opts - Additional data needed for themeing
 * @returns - An object of CSS Custom Properties used for themeing Button
 */
export const getButtonDesignTokens = (dt, {
  disabled,
  tokens,
  variant,
  density,
  isNewTokenValueFlagEnabled
}) => {
  let buttonTokens;
  const darkVariants = new Set([ButtonVariant.Primary, ButtonVariant.PrimarySmall, ButtonVariant.PrimaryLight, ButtonVariant.PrimaryIcon]);
  const disabledTokens = {
    'hover-bd': 'var(--button-bd)',
    'hover-bg': 'var(--button-bg)',
    'hover-fg': 'var(--button-fg)',
    'active-bd': 'var(--button-bd)',
    'active-bg': 'var(--button-bg)',
    'active-fg': 'var(--button-fg)'
  };
  if (variant === ButtonVariant.Primary || variant === ButtonVariant.PrimarySmall) {
    buttonTokens = {
      fg: dt.tokens['color-action-text-emphasis-normal'],
      bg: dt.tokens['color-action-background-emphasis-normal'],
      bd: dt.tokens['color-action-border-emphasis-normal'],
      'hover-fg': dt.tokens['color-action-text-emphasis-hover'],
      'hover-bg': dt.tokens['color-action-background-emphasis-hover'],
      'hover-bd': dt.tokens['color-action-border-emphasis-hover'],
      'active-fg': dt.tokens['color-action-text-emphasis-selected'],
      'active-bg': dt.tokens['color-action-background-emphasis-selected'],
      'active-bd': dt.tokens['color-action-border-emphasis-selected'],
      ...(disabled ? {
        fg: dt.tokens['color-action-text-emphasis-disabled'],
        bg: dt.tokens['color-action-background-emphasis-disabled'],
        bd: dt.tokens['color-action-border-emphasis-disabled'],
        ...disabledTokens
      } : {})
    };
  } else if (variant === ButtonVariant.PrimaryLight || variant === ButtonVariant.PrimaryIcon) {
    buttonTokens = {
      fg: dt.themeVariant === 'light' ? dt.tokens['color-text-primary-inverse'] : dt.tokens['color-text-primary'],
      bg: dt.tokens['color-action-background-subtle-normal'],
      bd: dt.tokens['color-action-border-subtle-normal'],
      'hover-fg': dt.tokens['color-action-text-emphasis-hover'],
      'hover-bg': dt.tokens['color-action-background-emphasis-hover'],
      'hover-bd': dt.tokens['color-action-border-emphasis-hover'],
      'active-fg': dt.tokens['color-action-text-emphasis-selected'],
      'active-bg': dt.tokens['color-action-background-emphasis-selected'],
      'active-bd': dt.tokens['color-action-border-emphasis-selected'],
      ...(disabled ? {
        fg: dt.tokens['color-action-text-emphasis-disabled'],
        bg: dt.tokens['color-action-background-emphasis-disabled'],
        bd: dt.tokens['color-action-border-emphasis-disabled'],
        ...disabledTokens
      } : {})
    };
  } else if (variant === ButtonVariant.Light || variant === ButtonVariant.Icon || variant === ButtonVariant.LightSmall || variant === ButtonVariant.SmallIcon) {
    buttonTokens = {
      fg: dt.tokens['color-action-text-subtle-normal'],
      bg: dt.tokens['color-action-background-subtle-normal'],
      bd: dt.tokens['color-action-border-subtle-normal'],
      'hover-fg': dt.tokens['color-action-text-subtle-hover'],
      'hover-bg': dt.tokens['color-action-background-subtle-hover'],
      'hover-bd': dt.tokens['color-action-border-subtle-hover'],
      'active-fg': dt.tokens['color-action-text-subtle-selected'],
      'active-bg': dt.tokens['color-action-background-subtle-selected'],
      'active-bd': dt.tokens['color-action-border-subtle-selected'],
      ...(disabled ? {
        fg: dt.tokens['color-action-text-subtle-disabled'],
        bg: dt.tokens['color-action-background-subtle-disabled'],
        bd: dt.tokens['color-action-border-subtle-disabled'],
        ...disabledTokens
      } : {})
    };
  } else {
    buttonTokens = {
      fg: dt.tokens['color-action-text-standard-normal'],
      bg: dt.tokens['color-action-background-standard-normal'],
      bd: dt.tokens['color-action-border-standard-normal'],
      'hover-fg': dt.tokens['color-action-text-standard-hover'],
      'hover-bg': dt.tokens['color-action-background-standard-hover'],
      'hover-bd': dt.tokens['color-action-border-standard-hover'],
      'active-fg': dt.tokens['color-action-text-standard-selected'],
      'active-bg': dt.tokens['color-action-background-standard-selected'],
      'active-bd': dt.tokens['color-action-border-standard-selected'],
      ...(disabled ? {
        fg: dt.tokens['color-action-text-standard-disabled'],
        bg: dt.tokens['color-action-background-standard-disabled'],
        bd: dt.tokens['color-action-border-standard-disabled'],
        ...disabledTokens
      } : {})
    };
  }
  let globalFocus;
  if (darkVariants.has(variant) && dt.themeVariant === 'light' && !disabled) {
    globalFocus = 'var(--focus-dark-bg)';
  } else {
    globalFocus = 'var(--focus-light-bg)';
  }
  const outlineOffset = 'calc(-2px + calc(-1 * var(--focus-width)))';
  return {
    ...renderDesignTokens({
      ...buttonTokens,
      ...(tokens ? tokens({
        disabled,
        state: ButtonState.Normal
      }) : undefined),
      ...{
        'animation-duration': dt.tokens['duration-100'],
        'animation-timing-function': dt.tokens['ease-in'],
        'animation-duration-hover': dt.tokens['duration-300'],
        'animation-timing-function-hover': dt.tokens['ease-in-out'],
        'font-size': density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m'],
        'font-size-s': dt.tokens['font-size-body-text-s'],
        'focus-outline': isNewTokenValueFlagEnabled && dt.themeVariant === 'dark' ? 'var(--focus-dark-bg)' : globalFocus,
        'focus-outline-active': dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg)' : globalFocus,
        'focus-outline-offset': outlineOffset,
        'border-radius-xs': dt.tokens['size-border-radius-xs']
      }
    }, 'button'),
    ...renderDesignTokens({
      ...(tokens ? tokens({
        disabled,
        state: ButtonState.Hover
      }) : undefined)
    }, 'button-hover'),
    ...renderDesignTokens({
      ...(tokens ? tokens({
        disabled,
        state: ButtonState.Active
      }) : undefined)
    }, 'button-active'),
    ...renderDesignTokens({
      m: dt.tokens['space-base-m'],
      s: dt.tokens['space-base-s'],
      xs: dt.tokens['space-base-xs'],
      xxs: dt.tokens['space-base-xxs'],
      pd: density === 'high' ? dt.tokens['space-squish-xs'] : dt.tokens['space-squish-s'],
      'pd-inline': density === 'high' ? dt.tokens['space-base-s'] : dt.tokens['space-base-m'],
      'min-block-size': density === 'high' ? 0 : '1.75rem'
    }, 'button-spacing'),
    ...renderDesignTokens({
      height: density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m'],
      width: density === 'high' ? dt.tokens['font-size-body-text-s'] : dt.tokens['font-size-body-text-m']
    }, 'icon'),
    ...(!dt.enableNovaTokens && getButtonLegacyDesignTokens(dt, {
      disabled,
      tokens,
      variant
    }))
  };
};

/**
 *
 * @param dt - DesignToken object
 * @param opts - Additional data needed for themeing
 * @returns - An object of CSS Custom Properties used for themeing Button with Legacy tokens
 * for Legacy report themes.
 */
export const getButtonLegacyDesignTokens = (dt, {
  disabled,
  tokens,
  variant
}) => {
  let stateTokens;
  if (variant === ButtonVariant.Primary || variant === ButtonVariant.PrimarySmall) {
    stateTokens = dt.statePrimary;
  } else if (variant === ButtonVariant.PrimaryLight || variant === ButtonVariant.PrimaryIcon) {
    stateTokens = dt.statePrimaryContent;
  } else if (variant === ButtonVariant.Light || variant === ButtonVariant.Icon || variant === ButtonVariant.LightSmall || variant === ButtonVariant.SmallIcon) {
    stateTokens = dt.stateStandardContent;
  } else {
    stateTokens = dt.stateStandard;
  }
  return {
    ...renderDesignTokens({
      ...(!disabled ? stateTokens.normal : stateTokens.disabled),
      ...(tokens ? tokens({
        disabled,
        state: ButtonState.Normal
      }) : undefined),
      ...{
        'font-size': dt.reportTokens.font.size.m,
        'font-size-s': dt.reportTokens.font.size.s
      }
    }, 'button'),
    ...renderDesignTokens({
      ...(!disabled ? stateTokens.hover : stateTokens.disabled),
      ...(tokens ? tokens({
        disabled,
        state: ButtonState.Hover
      }) : undefined)
    }, 'button-hover'),
    ...renderDesignTokens({
      ...(!disabled ? stateTokens.active : stateTokens.disabled),
      ...(tokens ? tokens({
        disabled,
        state: ButtonState.Active
      }) : undefined)
    }, 'button-active')
  };
};
//# sourceMappingURL=Button.js.map